/**
 * Helpers for endpoints that STREAM a generated file back.
 *
 * The legacy Hapi backend wrote exports to disk and answered with JSON
 * `{ data: { filePath } }`, which the frontend opened in a new tab. The NestJS
 * backend sends the bytes straight back with `Content-Type` and
 * `Content-Disposition` instead. Reading `filePath` off a binary body always
 * yields undefined, so a caller still written against the old shape silently
 * does nothing — which is exactly how the broken download buttons behaved.
 *
 * Call the endpoint with `responseType: "blob"` and hand the response here.
 */

/** MIME type per download type, used when re-wrapping the streamed bytes. */
const DOWNLOAD_MIME = {
  excel: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  xlsx: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
  pdf: "application/pdf",
};

/** File extension per download type. */
const DOWNLOAD_EXT = {
  excel: "xlsx",
  xlsx: "xlsx",
  pdf: "pdf",
};

export const mimeForType = (fileType) =>
  DOWNLOAD_MIME[fileType] || "application/octet-stream";

export const extensionForType = (fileType) => DOWNLOAD_EXT[fileType] || "bin";

/**
 * Prefer the name the server chose (Content-Disposition), fall back to ours.
 * Handles both `filename="x.xlsx"` and RFC 5987 `filename*=UTF-8''x.xlsx`.
 */
export const filenameFromResponse = (response, fallback) => {
  const disposition =
    response?.headers?.["content-disposition"] ||
    response?.headers?.["Content-Disposition"] ||
    "";
  const match = /filename\*?=(?:UTF-8'')?"?([^";]+)"?/i.exec(disposition);
  if (!match) return fallback;
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
};

/** Save a streamed response as a file in the browser. */
export const saveBlobResponse = (response, { fileType, fallbackName }) => {
  const blob = new Blob([response.data], { type: mimeForType(fileType) });
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");

  link.href = url;
  link.setAttribute("download", filenameFromResponse(response, fallbackName));
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
};

/**
 * Read the server's error message out of a failed blob request.
 *
 * With `responseType: "blob"` axios wraps the ERROR body in a Blob too, so
 * `error.response.data.message` is undefined and a real message like
 * "There is no EWS Data to Download!" would be swallowed by the generic
 * fallback. This reads it back out.
 */
export const readBlobErrorMessage = async (error, fallback) => {
  const data = error?.response?.data;

  if (data instanceof Blob) {
    try {
      const parsed = JSON.parse(await data.text());
      return parsed?.message || fallback;
    } catch {
      return fallback;
    }
  }

  return data?.message || fallback;
};
